import { getApiBaseUrl } from "../config/apiBase";
import { apiFetch } from "../config/apiFetch";
import { telanganaDistrictKeyFromStored } from "../data/telanganaDistricts";
import type { Application, ApplicationDossierPatch, HoBankingPatch, ValidationsPatch } from "../types";
import { emptyEngineerLinkage } from "../utils/engineer";

function normalizeApplicationFromApi(a: Application): Application {
  const raw = a.mobile?.photosFromMobile;
  const photos = Array.isArray(raw)
    ? raw
        .filter((p) => p && typeof (p as { url?: unknown }).url === "string")
        .map((p) => {
          const x = p as { label?: unknown; url: string };
          const url = String(x.url).trim();
          const label = String(x.label ?? "").trim() || url || "Photo";
          return { label, url };
        })
    : [];
  const districtKey = telanganaDistrictKeyFromStored(a.district);
  return {
    ...a,
    district: (districtKey ?? a.district) as Application["district"],
    engineer: a.engineer ?? emptyEngineerLinkage(),
    mobile: { ...a.mobile, photosFromMobile: photos },
  };
}

async function parseError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const j = JSON.parse(text) as { error?: string };
    if (j.error) return j.error;
  } catch {
    /* ignore */
  }
  return text || `Request failed (${res.status})`;
}

/** Load all applicants from MariaDB/MySQL (requires MYSQL_HOST). */
export async function fetchApplications(): Promise<Application[]> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/applications`, { cache: "no-store" });
  if (!res.ok) throw new Error(await parseError(res));
  const raw = (await res.json()) as Application[];
  return Array.isArray(raw) ? raw.map(normalizeApplicationFromApi) : [];
}

/** Save milestone phase completion to the server (persists milestone_completed_at without a full PUT). */
export async function saveMilestoneComplete(
  applicationId: string,
  templateId: string,
  done: boolean,
  completedAt?: string | null,
): Promise<void> {
  const res = await apiFetch(
    `${getApiBaseUrl()}/api/applications/${encodeURIComponent(applicationId)}/milestone-complete`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      cache: "no-store",
      body: JSON.stringify({ templateId, done, completedAt: completedAt ?? undefined }),
    },
  );
  if (!res.ok) throw new Error(await parseError(res));
}

/** Replace all applicant rows (snapshot sync after local edits). Returns server-normalized rows (no MOU base64). */
export async function saveApplicationsAll(applications: Application[]): Promise<Application[]> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/applications`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    cache: "no-store",
    body: JSON.stringify({ applications }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  const j = (await res.json()) as { applications?: Application[] };
  if (!Array.isArray(j.applications)) {
    throw new Error("Invalid server response: missing applications array");
  }
  return j.applications.map(normalizeApplicationFromApi);
}

/** Partial dossier update — only keys in `patch` are written (no full-row replace). */
export async function patchApplicationDossier(
  applicationId: string,
  patch: ApplicationDossierPatch,
): Promise<Application> {
  const id = String(applicationId ?? "").trim();
  if (!id) throw new Error("applicationId is required");
  const res = await apiFetch(`${getApiBaseUrl()}/api/applications/${encodeURIComponent(id)}/dossier`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    cache: "no-store",
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await parseError(res));
  const j = (await res.json()) as { application?: Application };
  if (!j.application || String(j.application.id ?? "").trim() !== id) {
    throw new Error("Invalid server response: missing application");
  }
  return normalizeApplicationFromApi(j.application);
}

/** Update only verification checklist, seeding confirmation, and/or hold reasons. */
export async function patchApplicationValidations(
  applicationId: string,
  patch: ValidationsPatch,
): Promise<Application> {
  const id = String(applicationId ?? "").trim();
  if (!id) throw new Error("applicationId is required");
  const res = await apiFetch(`${getApiBaseUrl()}/api/applications/${encodeURIComponent(id)}/validations`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    cache: "no-store",
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await parseError(res));
  const j = (await res.json()) as { application?: Application };
  if (!j.application || String(j.application.id ?? "").trim() !== id) {
    throw new Error("Invalid server response: missing application");
  }
  return normalizeApplicationFromApi(j.application);
}

/** Update only ICICI account / IFSC / branch and/or MOU on one dossier (does not touch other columns). */
export async function patchApplicationHoBanking(
  applicationId: string,
  patch: HoBankingPatch,
): Promise<Application> {
  const id = String(applicationId ?? "").trim();
  if (!id) throw new Error("applicationId is required");
  const res = await apiFetch(`${getApiBaseUrl()}/api/applications/${encodeURIComponent(id)}/ho-banking`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    cache: "no-store",
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await parseError(res));
  const j = (await res.json()) as { application?: Application };
  if (!j.application || String(j.application.id ?? "").trim() !== id) {
    throw new Error("Invalid server response: missing application");
  }
  return normalizeApplicationFromApi(j.application);
}

/** Upsert one applicant by id (does not delete other rows). Returns full normalized list from the server. */
export async function saveApplicationById(applicationId: string, application: Application): Promise<Application[]> {
  const id = String(applicationId ?? "").trim();
  if (!id || String(application.id ?? "").trim() !== id) {
    throw new Error("saveApplicationById: application.id must match applicationId");
  }
  const res = await apiFetch(`${getApiBaseUrl()}/api/applications/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    cache: "no-store",
    body: JSON.stringify({ application }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  const j = (await res.json()) as { applications?: Application[] };
  if (!Array.isArray(j.applications)) {
    throw new Error("Invalid server response: missing applications array");
  }
  return j.applications.map(normalizeApplicationFromApi);
}
